/**
 * Home — "Aurora" dashboard (D49).
 * Desktop: one large panel split in two — aurora wallet area (total, quick actions, chart area)
 * on the left, dark assets area (status card, Account/Vault tabs, asset tiles) on the right.
 * Phone/tablet: the same blocks stacked.
 * Totals, values and the chart come from live prices (Phase 8) in the chosen display currency.
 * Anything without a fresh price shows "—" — never an invented number.
 * Step 5 (D80): labelled actions, time-of-day greeting, hide-balances eye, asset rows with the
 * 24 h price change, placeholders while loading.
 */
import type { Balance, ChartRange, PortfolioSummary } from "@custos/api-client";
import {
  Badge,
  EmptyState,
  Notice,
  Segmented,
  Skeleton,
  Spot,
  cn,
  formatAmount,
  formatBps,
  formatFiat,
  isZeroAmount,
} from "@custos/ui";
import {
  ArrowDownLeft,
  ArrowLeftRight,
  ArrowUpRight,
  Clock3,
  Eye,
  EyeOff,
  Plus,
  UserRound,
} from "lucide-react";
import { useState } from "react";
import { Link } from "react-router";
import { AssetIcon } from "../components/bits";
import { PortfolioChart } from "../components/PortfolioChart";
import { VaultHomeTab } from "./Vault";
import { findChain, networkLabel } from "../lib/chains";
import { errorMessage } from "../lib/errors";
import { greeting } from "../lib/greeting";
import { useMask, usePrefs } from "../lib/prefs";
import {
  useBalances,
  useConfig,
  useDeposits,
  useMe,
  usePortfolio,
  usePortfolioHistory,
} from "../lib/queries";

const ACTIONS = [
  { to: "/withdraw", label: "Withdraw", icon: ArrowUpRight },
  { to: "/deposit", label: "Deposit", icon: ArrowDownLeft },
  { to: "/swap", label: "Swap", icon: ArrowLeftRight },
];

const RANGES: ChartRange[] = ["1d", "1w", "1m", "1y"];

export function HomePage() {
  const config = useConfig();
  const balances = useBalances();
  const deposits = useDeposits();
  const me = useMe();
  const currency = usePrefs((s) => s.currency);
  const [layer, setLayer] = useState<"account" | "vault">("account");
  const [range, setRange] = useState<ChartRange>("1d");
  const portfolio = usePortfolio(currency);
  const history = usePortfolioHistory(currency, range);
  const summary = portfolio.data;
  const noPrices = summary !== undefined && !summary.complete && summary.total === "0.00";
  const [showAll, setShowAll] = useState(false);

  const all = balances.data ?? [];
  const funded = all.filter((b) => !isZeroAmount(b.balance));
  const visible = showAll ? all : funded;
  const pending = (deposits.data ?? []).filter((d) => d.status === "DETECTED" || d.status === "CONFIRMING");
  const hide = usePrefs((s) => s.hideBalances);
  const setHide = usePrefs((s) => s.setHideBalances);
  const mask = useMask();
  // Rows sorted by value (unknown values last), like leading wallets.
  const sorted = [...visible].sort(
    (a, b) => Number(valueOf(summary, b) ?? -1) - Number(valueOf(summary, a) ?? -1),
  );

  return (
    <div className="lg:grid lg:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)] lg:overflow-hidden lg:rounded-[32px] lg:bg-surface">
      {/* ── Aurora wallet panel ── */}
      <section
        aria-label="Wallet"
        className="bg-aurora animate-rise relative overflow-hidden rounded-[32px] p-5 sm:p-8 lg:rounded-none lg:p-10"
      >
        <div className="flex items-center justify-between gap-3">
          <h1 className="truncate text-[22px] font-normal sm:text-2xl">{greeting()}</h1>
          <Link
            to="/activity"
            aria-label="Activity"
            className="glass-circle inline-flex size-12 shrink-0 items-center justify-center rounded-full"
          >
            <Clock3 className="size-5" aria-hidden />
          </Link>
        </div>

        <div className="mt-8 flex flex-col gap-6 text-center lg:mt-12 lg:text-left min-[1440px]:flex-row min-[1440px]:items-start min-[1440px]:justify-between">
          <div>
            <p className="flex items-center justify-center gap-2 text-base text-white/85 sm:text-lg lg:justify-start">
              Wallet ({currency})
              <button
                type="button"
                onClick={() => setHide(!hide)}
                aria-label={hide ? "Show balances" : "Hide balances"}
                aria-pressed={hide}
                title={hide ? "Show balances" : "Hide balances"}
                className="inline-flex size-11 items-center justify-center rounded-full text-white/85 hover:bg-white/12"
                data-testid="toggle-balances"
              >
                {hide ? <EyeOff className="size-5" aria-hidden /> : <Eye className="size-5" aria-hidden />}
              </button>
            </p>
            {portfolio.isPending ? (
              <span className="mx-auto mt-2 block h-14 w-56 animate-pulse rounded-2xl bg-white/15 sm:h-16 lg:mx-0" />
            ) : (
              <p
                className="mt-1 text-6xl leading-none font-normal tracking-[-0.04em] sm:text-7xl"
                data-testid="wallet-total"
              >
                {summary && !noPrices ? mask(formatFiat(summary.total, currency)) : "—"}
              </p>
            )}
            {summary && noPrices ? (
              <span className="mt-3 inline-flex rounded-full bg-white/12 px-3 py-1 text-sm text-white/90 backdrop-blur">
                Prices unavailable right now
              </span>
            ) : summary && !summary.complete ? (
              <span className="mt-3 inline-flex rounded-full bg-white/12 px-3 py-1 text-sm text-white/90 backdrop-blur">
                Some assets have no price yet
              </span>
            ) : (
              history.data?.changeBps != null && (
                <span
                  className="mt-3 inline-flex rounded-full bg-white/12 px-3 py-1 text-sm text-white/90 backdrop-blur"
                  data-testid="wallet-change"
                >
                  {history.data.changeBps >= 0 ? "▲ +" : "▼ "}
                  {formatBps(history.data.changeBps)} · {range}
                </span>
              )
            )}
          </div>

          <nav
            aria-label="Quick actions"
            className="flex items-start justify-center gap-2.5 lg:justify-start min-[1440px]:justify-end"
          >
            {ACTIONS.map((a) => (
              <Link
                key={a.to}
                to={a.to}
                aria-label={a.label}
                className="group flex w-16 shrink-0 flex-col items-center gap-1.5 lg:w-[4.5rem]"
              >
                <span className="glass-circle inline-flex size-14 items-center justify-center rounded-full transition-transform duration-200 group-active:scale-95 lg:size-16">
                  <a.icon className="size-6" aria-hidden />
                </span>
                <span className="text-xs text-white/85" aria-hidden>
                  {a.label}
                </span>
              </Link>
            ))}
            <Link
              to="/deposit"
              aria-label="Add funds"
              className="inline-flex h-14 min-w-20 flex-1 items-center justify-center gap-2 rounded-full bg-white px-5 text-base font-medium text-[#0B0A10] transition-transform duration-200 active:scale-95 sm:max-w-44 lg:h-16 lg:min-w-40"
            >
              <Plus className="size-6" strokeWidth={1.75} aria-hidden />
              <span className="hidden min-[400px]:inline">Add funds</span>
              <span className="min-[400px]:hidden">Add</span>
            </Link>
          </nav>
        </div>

        {/* Portfolio value over time (price snapshots every 5 minutes, D53). */}
        <div className="mt-8">
          <div
            role="radiogroup"
            aria-label="Chart range"
            className="flex justify-center gap-2 lg:justify-end"
          >
            {RANGES.map((r) => (
              <button
                key={r}
                type="button"
                role="radio"
                aria-checked={r === range}
                onClick={() => setRange(r)}
                className={cn(
                  "inline-flex h-10 min-w-14 items-center justify-center rounded-full px-3 text-sm transition-colors duration-200",
                  r === range
                    ? "animate-pop bg-primary text-on-primary"
                    : "bg-white/12 text-white/80 hover:bg-white/20",
                )}
              >
                {r}
              </button>
            ))}
          </div>
          <PortfolioChart
            history={
              history.data?.range === range && history.data.currency === currency ? history.data : undefined
            }
            currency={currency}
            className="mt-6 h-40 sm:h-56 lg:h-80"
          />
        </div>
      </section>

      {/* ── Assets panel ── */}
      <section aria-label="Assets" className="mt-5 flex flex-col gap-4 lg:mt-0 lg:p-8">
        <p className="flex min-w-0 items-center gap-2 text-sm text-fg/90">
          <UserRound className="size-4 shrink-0" aria-hidden />
          {me.data ? (
            <span className="truncate">{me.data.email}</span>
          ) : (
            <span className="h-4 w-48 animate-pulse rounded bg-hover" />
          )}
        </p>

        <div className="glass rounded-card border border-line p-5 lg:bg-soft">
          <p className="text-sm text-fg/85">Status</p>
          {deposits.isPending ? (
            <>
              <span className="mt-2 block h-7 w-44 animate-pulse rounded-lg bg-hover" />
              <span className="mt-2 block h-4 w-64 max-w-full animate-pulse rounded bg-hover" />
            </>
          ) : pending.length > 0 ? (
            <>
              <p className="mt-1 text-2xl font-medium text-fg">
                {pending.length} deposit{pending.length > 1 ? "s" : ""} confirming
              </p>
              <p className="mt-1 text-sm text-muted">
                {pending
                  .map(
                    (d) =>
                      `${formatAmount(d.amount)} ${d.asset} (${d.confirmations}/${d.requiredConfirmations})`,
                  )
                  .join(", ")}{" "}
                — added to your balance once confirmed.
              </p>
            </>
          ) : (
            <>
              <p className="mt-1 text-2xl font-medium text-fg">All settled</p>
              <p className="mt-1 text-sm text-muted">No deposits waiting for confirmations.</p>
            </>
          )}
        </div>

        <Segmented
          label="Wallet layer"
          value={layer}
          onChange={setLayer}
          className="grid w-full grid-cols-2 [&>button]:w-full"
          options={[
            { value: "account", label: "Account" },
            { value: "vault", label: "Vault" },
          ]}
        />

        {layer === "vault" ? (
          <VaultHomeTab />
        ) : balances.isPending ? (
          <div className="flex flex-col gap-2">
            {[0, 1, 2].map((i) => (
              <Skeleton key={i} className="h-[4.5rem] rounded-card" />
            ))}
          </div>
        ) : balances.error ? (
          <Notice tone="danger">{errorMessage(balances.error)}</Notice>
        ) : visible.length === 0 ? (
          <div className="glass rounded-card border border-line lg:bg-soft">
            <EmptyState icon={<Spot name="coins" />} title="No funds yet">
              Deposit testnet coins to get started.{" "}
              <Link to="/deposit" className="text-primary-strong hover:underline">
                Get a deposit address
              </Link>
            </EmptyState>
          </div>
        ) : (
          <>
            <p className="-mb-1 text-xs text-muted">Custodial — held by the service for you.</p>
            <ul
              className="stagger flex flex-col divide-y divide-line overflow-hidden rounded-card border border-line bg-surface lg:bg-soft"
              data-testid="balances"
            >
              {sorted.map((b) => (
                <AssetRow
                  key={`${b.chain}:${b.asset}`}
                  balance={b}
                  value={valueOf(summary, b)}
                  change={
                    summary?.assets.find((a) => a.chain === b.chain && a.asset === b.asset)?.change24hBps ??
                    null
                  }
                  currency={currency}
                  mask={mask}
                  network={networkLabel(findChain(config.data?.chains ?? [], b.chain), b.chain)}
                />
              ))}
            </ul>
          </>
        )}

        {layer === "account" && all.length > funded.length && (
          <button
            type="button"
            onClick={() => setShowAll((v) => !v)}
            className="mx-auto min-h-11 rounded-full px-4 text-sm text-fg/90 hover:bg-hover"
          >
            {showAll ? "Show less" : "See more"}
          </button>
        )}
      </section>
    </div>
  );
}

function valueOf(summary: PortfolioSummary | undefined, b: Balance): string | null {
  return summary?.assets.find((a) => a.chain === b.chain && a.asset === b.asset)?.value ?? null;
}

/** 24 h price change chip: green up, red down, muted when unknown. */
function ChangeChip({ bps }: { bps: number | null }) {
  if (bps === null) return <span className="text-xs text-muted">—</span>;
  if (bps === 0)
    return (
      <span className="font-mono text-xs text-muted tabular-nums" data-testid="asset-change">
        0%
      </span>
    );
  const up = bps >= 0;
  return (
    <span
      className={cn("font-mono text-xs tabular-nums", up ? "text-success" : "text-danger")}
      data-testid="asset-change"
      title="Price change in the last 24 hours"
    >
      {up ? "+" : ""}
      {formatBps(bps)}
    </span>
  );
}

function AssetRow({
  balance,
  network,
  value,
  change,
  currency,
  mask,
}: {
  balance: Balance;
  network: string;
  value: string | null;
  change: number | null;
  currency: string;
  mask: (s: string) => string;
}) {
  return (
    <li>
      <Link
        to={`/assets/${balance.chain}/${balance.asset}`}
        className="flex min-h-[4.5rem] items-center gap-3 px-4 py-3 transition-colors duration-200 hover:bg-hover"
      >
        <AssetIcon symbol={balance.asset} />
        <div className="min-w-0 flex-1">
          <p className="flex items-center gap-2 text-base font-medium text-fg">
            <span className="truncate">{balance.name}</span>
            <Badge>{balance.asset}</Badge>
          </p>
          <p className="truncate text-sm text-muted">{network}</p>
        </div>
        <div className="flex flex-col items-end gap-0.5 text-right">
          <span className="font-mono text-sm text-fg tabular-nums" data-testid="asset-value">
            {value === null ? "—" : mask(formatFiat(value, currency))}
          </span>
          <span className="font-mono text-xs text-muted tabular-nums">
            {mask(formatAmount(balance.balance))} {balance.asset}
          </span>
          <ChangeChip bps={change} />
        </div>
      </Link>
    </li>
  );
}
